import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle, DollarSign, CreditCard, Lock, 
  ChevronLeft, ArrowRight, Trophy, Shield
} from 'lucide-react';

const PaymentPage = () => {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [phase, setPhase] = useState(null);
  const [payment, setPayment] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const organizationId = location.state?.organizationId || localStorage.getItem('organization_id') || 'b8b52a08-165e-4a21-85ae-099e7268a29e';

  useEffect(() => {
    fetchPhase();
    fetchPaymentStatus();
  }, []);

  const fetchPhase = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment-phases/${assessmentId}/${organizationId}`);
      const data = await response.json();
      setPhase(data);
    } catch (error) {
      console.error('Error fetching phase:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/${assessmentId}/${organizationId}`);
      const data = await response.json();
      setPayment(data);
    } catch (error) {
      console.error('Error fetching payment:', error);
    }
  };

  const handleInitiatePayment = async () => {
    setProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assessment_id: assessmentId,
          organization_id: organizationId,
          amount: 500.00
        })
      });

      if (response.ok) {
        const result = await response.json();
        setPayment(result);
        
        // Simulate payment gateway redirect (placeholder)
        setTimeout(() => {
          handleCompletePayment(result.payment_id);
        }, 2000);
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      setProcessing(false);
    }
  };

  const handleCompletePayment = async (paymentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_id: paymentId || payment.payment_id,
          transaction_id: 'TXN-' + Date.now()
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Show success message and redirect
        setTimeout(() => {
          navigate(`/org-assessment/quantitative/${assessmentId}`, {
            state: { organizationId }
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error completing payment:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  const isPaymentCompleted = payment?.status === 'completed' || phase?.payment_status === 'completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/org-admin/assessments')}
              className="p-2 hover:bg-white/20 rounded-lg transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-black">Assessment Payment</h1>
              <p className="text-green-100">Complete payment to unlock quantitative assessment</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Side - Completion Info */}
          <div className="md:col-span-2 space-y-6">
            {/* Qualitative Completion Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900">Qualitative Assessment Complete!</h2>
                  <p className="text-gray-600">You've successfully completed all 60 questions</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-semibold mb-1">Questions Answered</p>
                  <p className="text-3xl font-black text-blue-900">80/80</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-semibold mb-1">Pillars Covered</p>
                  <p className="text-3xl font-black text-purple-900">8/8</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border-l-4 border-blue-500">
                <p className="text-sm text-gray-700">
                  <strong>Next Step:</strong> Complete the payment to unlock the Quantitative Assessment phase. 
                  Once payment is confirmed, you'll gain immediate access to continue your certification journey.
                </p>
              </div>
            </div>

            {/* What's Included */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
                What's Included in Your Assessment
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Quantitative Assessment Access</p>
                    <p className="text-sm text-gray-600">Complete data-driven evaluation across all 6 pillars</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Expert Evaluator Review</p>
                    <p className="text-sm text-gray-600">Professional scoring using detailed rubrics</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Certification & Badge</p>
                    <p className="text-sm text-gray-600">Aspiring, Inspired, or Luminary tier certification</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Comprehensive Report</p>
                    <p className="text-sm text-gray-600">Detailed pillar-wise scores and recommendations</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Official Certificate</p>
                    <p className="text-sm text-gray-600">Downloadable certificate with unique ID</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Payment Card */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden sticky top-8">
              {/* Payment Header */}
              <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <DollarSign className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="text-2xl font-black text-center">Assessment Fee</h3>
              </div>

              {/* Payment Amount */}
              <div className="p-8">
                <div className="text-center mb-8">
                  <p className="text-gray-600 mb-2">One-time payment</p>
                  <p className="text-5xl font-black text-gray-900">$500</p>
                  <p className="text-sm text-gray-500 mt-2">USD</p>
                </div>

                {isPaymentCompleted ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                      <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                      <p className="font-bold text-green-900 mb-2">Payment Successful!</p>
                      <p className="text-sm text-green-700">Transaction ID: {phase?.payment_transaction_id || 'TXN-XXXXX'}</p>
                    </div>
                    
                    <button
                      onClick={() => navigate(`/org-assessment/quantitative/${assessmentId}`, { state: { organizationId } })}
                      className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-4 rounded-lg font-bold hover:from-green-700 hover:to-teal-700 transition-all flex items-center justify-center gap-2"
                    >
                      Continue to Quantitative
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Payment Method (Placeholder) */}
                    <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-6 h-6 text-gray-400" />
                          <div>
                            <p className="font-semibold text-gray-900">Payment Gateway</p>
                            <p className="text-xs text-gray-500">Placeholder Integration</p>
                          </div>
                        </div>
                        <Lock className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>

                    <button
                      onClick={handleInitiatePayment}
                      disabled={processing}
                      className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-4 rounded-lg font-bold hover:from-green-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {processing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5" />
                          Proceed to Payment
                        </>
                      )}
                    </button>

                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                      <Shield className="w-4 h-4" />
                      <span>Secure payment processing</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Security Notice */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <p className="text-xs text-gray-600 text-center">
                  <strong>Note:</strong> This is a placeholder payment page. In production, integrate with Stripe, PayPal, or your preferred payment gateway.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
